import {
	routeCalendarM1,
	routeCalendarM2,
} from "@/setup/routes/calendar.routes";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import { Box, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useRef } from "react";
import { ProductionEventSummary } from "../models/SalesOverview";
import { formatNumber } from "./utils";

type DayEnding = {
	eventId: number;
	sectionName: string;
	actual: number | null;
	scheduled: number;
	tph: number;
};

const ACTUAL = "Actual";
const ACTUAL_PROJECTED = "ActualProjected";
const SCHEDULED = "Scheduled";

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

const monthBounds = () => {
	const now = new Date();
	const start = new Date(now.getFullYear(), now.getMonth(), 1);
	const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
	const daysInMonth = Math.round((end.getTime() - start.getTime()) / DAY_MS);
	return { start, end, daysInMonth, now };
};

const dayOfMonth = (d: Date, monthStart: Date) => {
	const diff = Math.floor((d.getTime() - monthStart.getTime()) / DAY_MS);
	return diff + 1;
};

export const CumulativeTonsChart = ({
	events,
	millId,
}: {
	events: ProductionEventSummary[];
	millId: number;
}) => {
	const {
		rows,
		allDots,
		pastDots,
		futureDots,
		endingsByDay,
		daysInMonth,
		eventDayList,
		stepActualCum,
		stepScheduledCum,
	} = (() => {
		const { start, end: monthEnd, daysInMonth, now } = monthBounds();
		const monthStartMs = start.getTime();
		const monthEndMs = monthEnd.getTime();
		const todayDay = Math.min(Math.max(dayOfMonth(now, start), 1), daysInMonth);

		const actualPerDay = new Array(daysInMonth).fill(0) as number[];
		const scheduledPerDay = new Array(daysInMonth).fill(0) as number[];
		const endingsByDay = new Map<number, DayEnding[]>();

		for (const e of events) {
			const rawStartMs = e.EventStart.getTime();
			const rawEndMs = (e.EventEnd ?? now).getTime();
			const eStartMs = Math.max(rawStartMs, monthStartMs);
			const eEndMs = Math.min(rawEndMs, monthEndMs);
			if (eEndMs <= eStartMs) continue;

			const spanMs = eEndMs - eStartMs;
			const actualRaw = e.ActualFinishedTons ?? null;
			const actual = actualRaw ?? 0;
			const scheduled = e.ScheduledFinishedTons ?? 0;

			const firstIdx = Math.max(
				Math.floor((eStartMs - monthStartMs) / DAY_MS),
				0,
			);
			const lastIdx = Math.min(
				Math.floor((eEndMs - monthStartMs - 1) / DAY_MS),
				daysInMonth - 1,
			);

			for (let i = firstIdx; i <= lastIdx; i++) {
				const dayStartMs = monthStartMs + i * DAY_MS;
				const dayEndMs = dayStartMs + DAY_MS;
				const overlap =
					Math.min(dayEndMs, eEndMs) - Math.max(dayStartMs, eStartMs);
				if (overlap <= 0) continue;
				const frac = overlap / spanMs;
				actualPerDay[i] += actual * frac;
				scheduledPerDay[i] += scheduled * frac;
			}

			if (e.EventEnd && (actual > 0 || scheduled > 0)) {
				const endDay = Math.min(
					Math.max(dayOfMonth(e.EventEnd, start), 1),
					daysInMonth,
				);
				const totalHours = (rawEndMs - rawStartMs) / HOUR_MS;
				const tonsForTph = actual > 0 ? actual : scheduled;
				const tph = totalHours > 0 ? tonsForTph / totalHours : 0;
				const list = endingsByDay.get(endDay) ?? [];
				list.push({
					eventId: e.EventId,
					sectionName: e.SectionName ?? `#${e.EventId}`,
					actual: actualRaw,
					scheduled,
					tph,
				});
				endingsByDay.set(endDay, list);
			}
		}

		let lastActualDay = 0;
		for (let i = daysInMonth - 1; i >= 0; i--) {
			if (actualPerDay[i] > 0.0001) {
				lastActualDay = i + 1;
				break;
			}
		}

		const rows: (string | number)[][] = [["day", "category", "tons"]];
		let aRun = 0;
		let sRun = 0;
		let actualAtCutoff = 0;
		for (let i = 0; i < daysInMonth; i++) {
			const day = i + 1;
			aRun += actualPerDay[i];
			sRun += scheduledPerDay[i];
			if (day <= lastActualDay) {
				rows.push([day, ACTUAL, Math.round(aRun)]);
				if (day === lastActualDay) actualAtCutoff = Math.round(aRun);
			}
			rows.push([day, SCHEDULED, Math.round(sRun)]);
		}

		const stepActualCum = new Array(daysInMonth + 1).fill(0) as number[];
		const stepScheduledCum = new Array(daysInMonth + 1).fill(0) as number[];
		let stepActual = 0;
		let stepScheduled = 0;
		for (let day = 1; day <= daysInMonth; day++) {
			const endings = endingsByDay.get(day);
			if (endings) {
				for (const e of endings) {
					stepActual += e.actual ?? 0;
					stepScheduled += e.scheduled;
				}
			}
			stepActualCum[day] = stepActual;
			stepScheduledCum[day] = stepScheduled;
		}

		const projectedStart = Math.max(lastActualDay, 1);
		const projectedEnd = Math.max(projectedStart, todayDay);
		for (let day = projectedStart; day <= projectedEnd; day++) {
			rows.push([day, ACTUAL_PROJECTED, actualAtCutoff]);
		}

		const pastDots: [number, number][] = [];
		const futureDots: [number, number][] = [];
		const allDots: [number, number][] = [];
		let runningActual = 0;
		let runningScheduled = 0;
		for (let i = 0; i < daysInMonth; i++) {
			const day = i + 1;
			runningActual += actualPerDay[i];
			runningScheduled += scheduledPerDay[i];
			if (!endingsByDay.has(day)) continue;
			allDots.push([day, Math.round(runningScheduled)]);
			if (day <= lastActualDay) {
				pastDots.push([day, Math.round(runningActual)]);
			} else if (day <= todayDay) {
				futureDots.push([day, actualAtCutoff]);
			}
		}

		const eventDayList = Array.from(endingsByDay.keys()).sort((a, b) => a - b);

		return {
			rows,
			allDots,
			pastDots,
			futureDots,
			endingsByDay,
			daysInMonth,
			eventDayList,
			stepActualCum,
			stepScheduledCum,
		};
	})();

	const nearestEventDay = (day: number) => {
		if (eventDayList.length === 0) return null;
		let best = eventDayList[0];
		let bestDiff = Math.abs(day - best);
		for (const d of eventDayList) {
			const diff = Math.abs(day - d);
			if (diff < bestDiff) {
				best = d;
				bestDiff = diff;
			}
		}
		return best;
	};

	const stateRef = useRef({
		endingsByDay,
		eventDayList,
		daysInMonth,
	});
	stateRef.current = { endingsByDay, eventDayList, daysInMonth };

	const chartEvents = {
		click: (params: any) => {
			const native = params?.event?.event as MouseEvent | undefined;
			if (!native?.ctrlKey && !native?.metaKey) return;

			const value = params?.value;
			const hoveredDay = Array.isArray(value) ? Number(value[0]) : undefined;
			if (!hoveredDay) return;

			const target = endingsByDay.has(hoveredDay)
				? hoveredDay
				: nearestEventDay(hoveredDay);
			if (target == null) return;

			const eventId = endingsByDay.get(target)?.[0]?.eventId;
			if (eventId == null) return;

			const base = millId === MILL_1.id ? routeCalendarM1 : routeCalendarM2;
			window.open(`${base}?eventId=${eventId}`, "_blank");
		},
	};

	const handleChartReady = (chart: any) => {
		const zr = chart.getZr?.();
		if (!zr) return;
		zr.on("mousemove", (ev: any) => {
			const point: [number, number] = [ev.offsetX, ev.offsetY];
			if (!chart.containPixel("grid", point)) return;
			const converted = chart.convertFromPixel("grid", point) as
				| [number, number]
				| null;
			if (!converted) return;
			const rawDay = Math.round(converted[0]);
			const { endingsByDay: em, eventDayList: ed } = stateRef.current;
			if (ed.length === 0 || em.has(rawDay)) return;

			let best = ed[0];
			let bestDiff = Math.abs(rawDay - best);
			for (const d of ed) {
				const diff = Math.abs(rawDay - d);
				if (diff < bestDiff) {
					best = d;
					bestDiff = diff;
				}
			}

			const pixel = chart.convertToPixel("grid", [best, 0]) as
				| [number, number]
				| null;
			if (!pixel) return;
			chart.dispatchAction({
				type: "showTip",
				x: pixel[0],
				y: point[1],
			});
		});
	};

	const renderTooltip = (day: number) => {
		const endings = endingsByDay.get(day);
		const actualCum = Math.round(stepActualCum[day] ?? 0);
		const scheduledCum = Math.round(stepScheduledCum[day] ?? 0);
		const hasActual = actualCum > 0;

		let out = `<div style="font-size:0.75rem;color:#333;"><b>Day ${day}</b></div>`;
		out += `<div style="margin-top:2px;font-size:0.72rem;color:#333;">`;
		if (hasActual) {
			out += `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#3f51b5;margin-right:4px;"></span>`;
			out += `Actual: <b>${formatNumber(actualCum, 0)}</b> tons<br/>`;
		}
		out += `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#8BC34A;margin-right:4px;"></span>`;
		out += `Scheduled: <b>${formatNumber(scheduledCum, 0)}</b> tons`;
		out += `</div>`;

		if (endings && endings.length > 0) {
			const thStyle =
				"text-align:right;padding:2px 0 2px 10px;font-weight:500;color:#666;";
			const thFirst =
				"text-align:left;padding:2px 10px 2px 0;font-weight:500;color:#666;";
			const tdStyle = "text-align:right;padding:2px 0 2px 10px;color:#333;";
			const tdFirst = "text-align:left;padding:2px 10px 2px 0;color:#333;";

			out += `<table style="border-collapse:collapse;margin-top:6px;font-size:0.72rem;">`;
			out += `<thead><tr style="border-bottom:1px solid #eee;">`;
			out += `<th style="${thFirst}">Section</th>`;
			out += `<th style="${thStyle}">Actual</th>`;
			out += `<th style="${thStyle}">Scheduled</th>`;
			out += `<th style="${thStyle}">TPH</th>`;
			out += `</tr></thead><tbody>`;
			for (const e of endings) {
				out += `<tr>`;
				out += `<td style="${tdFirst}">${e.sectionName}</td>`;
				out += `<td style="${tdStyle}">${formatNumber(e.actual, 0)}</td>`;
				out += `<td style="${tdStyle}">${formatNumber(e.scheduled, 0)}</td>`;
				out += `<td style="${tdStyle}">${formatNumber(e.tph, 1)}</td>`;
				out += `</tr>`;
			}
			out += `</tbody></table>`;
			out += `<div style="margin-top:4px;font-size:0.68rem;color:#999;">Ctrl+click to open on calendar</div>`;
		}
		return out;
	};

	const option = {
		tooltip: {
			trigger: "axis",
			axisPointer: { type: "line" },
			formatter: (params: any[]) => {
				const rawDay = Number(params?.[0]?.axisValue);
				if (Number.isNaN(rawDay)) return "";
				const day = endingsByDay.has(rawDay) ? rawDay : nearestEventDay(rawDay);
				if (day == null) return "";
				return renderTooltip(day);
			},
		},
		legend: {
			data: [ACTUAL, SCHEDULED],
			right: 10,
			top: 0,
			textStyle: { fontSize: 10 },
		},
		grid: { top: 24, left: 8, right: 8, bottom: 5, containLabel: true },
		dataset: [
			{ source: rows },
			{
				transform: {
					type: "filter",
					config: { dimension: "category", "=": ACTUAL },
				},
			},
			{
				transform: {
					type: "filter",
					config: { dimension: "category", "=": SCHEDULED },
				},
			},
			{
				transform: {
					type: "filter",
					config: { dimension: "category", "=": ACTUAL_PROJECTED },
				},
			},
		],
		xAxis: {
			type: "value",
			min: 1,
			max: daysInMonth,
			minInterval: 1,
			axisLabel: { fontSize: 9, color: "#999" },
			axisTick: { show: false },
			axisLine: { lineStyle: { color: "#ddd" } },
		},
		yAxis: {
			type: "value",
			axisLabel: {
				fontSize: 9,
				color: "#999",
				formatter: (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
			},
			splitLine: { lineStyle: { color: "#f0f0f0" } },
		},
		series: [
			{
				name: SCHEDULED,
				type: "line",
				datasetIndex: 2,
				encode: { x: "day", y: "tons" },
				smooth: true,
				showSymbol: false,
				lineStyle: { color: "#8BC34A", width: 2 },
				itemStyle: { color: "#8BC34A" },
				z: 1,
			},
			{
				name: "Event ended",
				type: "scatter",
				data: allDots,
				symbol: "circle",
				symbolSize: 3,
				itemStyle: {
					color: "#fff",
					borderColor: "#8BC34A",
					borderWidth: 2,
				},
				z: 3,
				tooltip: { show: false },
				cursor: "pointer",
			},
			{
				name: "ActualProjected",
				type: "line",
				datasetIndex: 3,
				encode: { x: "day", y: "tons" },
				showSymbol: false,
				lineStyle: { color: "#BDBDBD", width: 2, type: "dashed" },
				itemStyle: { color: "#BDBDBD" },
				silent: true,
				tooltip: { show: false },
				z: 1,
			},
			{
				name: ACTUAL,
				type: "line",
				datasetIndex: 1,
				encode: { x: "day", y: "tons" },
				smooth: true,
				showSymbol: false,
				lineStyle: { color: "#3f51b5", width: 2 },
				itemStyle: { color: "#3f51b5" },
				z: 2,
			},
			{
				name: "Event ended",
				type: "scatter",
				data: pastDots,
				symbol: "circle",
				symbolSize: 3,
				itemStyle: {
					color: "#fff",
					borderColor: "#1a237e",
					borderWidth: 2,
				},
				z: 3,
				tooltip: { show: false },
				cursor: "pointer",
			},
			{
				name: "Event scheduled",
				type: "scatter",
				data: futureDots,
				symbol: "circle",
				symbolSize: 3,
				itemStyle: {
					color: "#fff",
					borderColor: "#9E9E9E",
					borderWidth: 2,
				},
				z: 3,
				tooltip: { show: false },
				cursor: "pointer",
			},
		],
	};

	return (
		<Box>
			<Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
				Cumulative tons · this month
			</Typography>
			<ReactECharts
				option={option}
				onEvents={chartEvents}
				onChartReady={handleChartReady}
				style={{ height: 160, width: "100%" }}
				opts={{ renderer: "svg" }}
				notMerge={true}
			/>
		</Box>
	);
};
